import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { productApi } from '../../api/product';

// 模拟商品数据
const MOCK_PRODUCT_DATA = [
  {
    id: 1,
    name: '石佛寺文化纪念品',
    category: '纪念品',
    price: 99.99,
    stock: 100,
    status: 'ACTIVE',
    auditStatus: 'APPROVED'
  },
  {
    id: 2,
    name: '佛教工艺画',
    category: '艺术品',
    price: 199.99,
    stock: 50,
    status: 'INACTIVE',
    auditStatus: 'PENDING'
  },
  {
    id: 3,
    name: '佛珠手链',
    category: '饰品',
    price: 59.99,
    stock: 200,
    status: 'ACTIVE',
    auditStatus: 'REJECTED'
  }
];

// 模拟分类数据
const MOCK_CATEGORY_DATA = [
  {
    id: 1,
    name: '纪念品',
    code: 'memorial',
    createTime: '2023-01-01 10:00:00',
    updateTime: '2023-01-01 10:00:00'
  },
  {
    id: 2,
    name: '艺术品',
    code: 'art',
    createTime: '2023-01-02 11:00:00',
    updateTime: '2023-01-02 11:00:00'
  },
  {
    id: 3,
    name: '饰品',
    code: 'accessory',
    createTime: '2023-01-03 12:00:00',
    updateTime: '2023-01-03 12:00:00'
  }
];

// 获取商品列表
export const fetchProductList = createAsyncThunk(
  'product/fetchList',
  async ({ page = 1, pageSize = 10, ...params }, { rejectWithValue }) => {
    try {
      const response = await productApi.getProductList({ page, pageSize, ...params });
      return response.data;
    } catch (error) {
      console.warn('获取商品列表失败，使用模拟数据', error);
      
      // 模拟搜索功能
      const filteredData = MOCK_PRODUCT_DATA.filter(product => {
        // 名称搜索
        const nameMatch = !params.name || 
          product.name.toLowerCase().includes(params.name.toLowerCase());
        
        // 分类搜索
        const categoryMatch = !params.category || 
          product.category === params.category;
        
        // 状态搜索
        const statusMatch = !params.status || 
          product.status === params.status;
        
        // 审核状态搜索
        const auditStatusMatch = !params.auditStatus || 
          product.auditStatus === params.auditStatus;
        
        return nameMatch && categoryMatch && statusMatch && auditStatusMatch;
      });

      // 模拟分页
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = filteredData.slice(startIndex, endIndex);
      
      return {
        list: paginatedData,
        total: filteredData.length,
        page,
        pageSize
      };
    }
  }
);

// 创建商品
export const createProduct = createAsyncThunk(
  'product/create',
  async (productData, { rejectWithValue }) => {
    try {
      // 处理文件上传
      const formData = new FormData();
      Object.keys(productData).forEach(key => {
        if (key === 'productImage' && productData[key]?.length) {
          formData.append('productImage', productData[key][0].originFileObj);
        } else {
          formData.append(key, productData[key]);
        }
      });

      const response = await productApi.createProduct(formData);
      return response.data;
    } catch (error) {
      console.warn('创建商品失败，使用模拟数据', error);
      const newProduct = {
        id: MOCK_PRODUCT_DATA.length + 1,
        ...productData,
        status: 'ACTIVE',
        auditStatus: 'PENDING',
        imageUrl: productData.productImage 
          ? URL.createObjectURL(productData.productImage[0].originFileObj) 
          : `https://example.com/mock-product-${MOCK_PRODUCT_DATA.length + 1}.jpg`
      };
      MOCK_PRODUCT_DATA.push(newProduct);
      return newProduct;
    }
  }
);

// 更新商品
export const updateProduct = createAsyncThunk(
  'product/update',
  async ({ id, ...productData }, { rejectWithValue }) => {
    try {
      // 处理文件上传
      const formData = new FormData();
      Object.keys(productData).forEach(key => {
        if (key === 'productImage' && productData[key]?.length) {
          formData.append('productImage', productData[key][0].originFileObj);
        } else {
          formData.append(key, productData[key]);
        }
      });

      const response = await productApi.updateProduct(id, formData);
      return response.data;
    } catch (error) {
      console.warn('更新商品失败，使用模拟数据', error);
      const index = MOCK_PRODUCT_DATA.findIndex(p => p.id === id);
      if (index !== -1) {
        const updatedProduct = { 
          ...MOCK_PRODUCT_DATA[index], 
          ...productData,
          imageUrl: productData.productImage 
            ? URL.createObjectURL(productData.productImage[0].originFileObj) 
            : MOCK_PRODUCT_DATA[index].imageUrl
        };
        MOCK_PRODUCT_DATA[index] = updatedProduct;
        return updatedProduct;
      }
      return rejectWithValue('商品不存在');
    }
  }
);

// 删除商品
export const deleteProduct = createAsyncThunk(
  'product/delete',
  async (id, { rejectWithValue }) => {
    try {
      await productApi.deleteProduct(id);
      return id;
    } catch (error) {
      console.warn('删除商品失败，使用模拟数据', error);
      const index = MOCK_PRODUCT_DATA.findIndex(p => p.id === id);
      if (index !== -1) {
        MOCK_PRODUCT_DATA.splice(index, 1);
        return id;
      }
      return rejectWithValue('商品不存在');
    }
  }
);

// 切换商品状态
export const toggleProductStatus = createAsyncThunk(
  'product/toggleStatus',
  async (id, { rejectWithValue }) => {
    try {
      const response = await productApi.toggleProductStatus(id);
      return response.data;
    } catch (error) {
      console.warn('切换商品状态失败，使用模拟数据', error);
      const index = MOCK_PRODUCT_DATA.findIndex(p => p.id === id);
      if (index !== -1) {
        const currentStatus = MOCK_PRODUCT_DATA[index].status;
        MOCK_PRODUCT_DATA[index].status = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        return MOCK_PRODUCT_DATA[index];
      }
      return rejectWithValue('商品不存在');
    }
  }
);

// 商品审核
export const auditProduct = createAsyncThunk(
  'product/audit',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await productApi.auditProduct(id, status);
      return response.data;
    } catch (error) {
      console.warn('商品审核失败，使用模拟数据', error);
      const index = MOCK_PRODUCT_DATA.findIndex(p => p.id === id);
      if (index !== -1) {
        // 更新模拟数据的审核状态
        MOCK_PRODUCT_DATA[index].auditStatus = status;
        
        // 如果审核通过，可以额外处理上架逻辑
        if (status === 'APPROVED') {
          MOCK_PRODUCT_DATA[index].status = 'ACTIVE';
        }
        
        return MOCK_PRODUCT_DATA[index];
      }
      return rejectWithValue('商品不存在');
    }
  }
);

// 分类管理相关的异步 Thunk
export const fetchCategoryList = createAsyncThunk(
  'category/fetchList',
  async ({ page = 1, pageSize = 10, ...params }, { rejectWithValue }) => {
    try {
      const response = await productApi.getCategoryList({ page, pageSize, ...params });
      return response.data;
    } catch (error) {
      console.warn('获取分类列表失败，使用模拟数据', error);
      
      // 模拟搜索功能
      const filteredData = MOCK_CATEGORY_DATA.filter(category => {
        const nameMatch = !params.name || 
          category.name.toLowerCase().includes(params.name.toLowerCase());
        
        const codeMatch = !params.code || 
          category.code.toLowerCase().includes(params.code.toLowerCase());
        
        return nameMatch && codeMatch;
      });

      // 模拟分页
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = filteredData.slice(startIndex, endIndex);
      
      return {
        list: paginatedData,
        total: filteredData.length,
        page,
        pageSize
      };
    }
  }
);

// 创建分类
export const createCategory = createAsyncThunk(
  'category/create',
  async (categoryData, { rejectWithValue }) => {
    try {
      const response = await productApi.createCategory(categoryData);
      return response.data;
    } catch (error) {
      console.warn('创建分类失败，使用模拟数据', error);
      const newCategory = {
        id: MOCK_CATEGORY_DATA.length + 1,
        ...categoryData,
        createTime: new Date().toLocaleString(),
        updateTime: new Date().toLocaleString()
      };
      MOCK_CATEGORY_DATA.push(newCategory);
      return newCategory;
    }
  }
);

// 更新分类
export const updateCategory = createAsyncThunk(
  'category/update',
  async ({ id, ...categoryData }, { rejectWithValue }) => {
    try {
      const response = await productApi.updateCategory(id, categoryData);
      return response.data;
    } catch (error) {
      console.warn('更新分类失败，使用模拟数据', error);
      const index = MOCK_CATEGORY_DATA.findIndex(c => c.id === id);
      if (index !== -1) {
        const updatedCategory = { 
          ...MOCK_CATEGORY_DATA[index], 
          ...categoryData,
          updateTime: new Date().toLocaleString()
        };
        MOCK_CATEGORY_DATA[index] = updatedCategory;
        return updatedCategory;
      }
      return rejectWithValue('分类不存在');
    }
  }
);

// 删除分类
export const deleteCategory = createAsyncThunk(
  'category/delete',
  async (id, { rejectWithValue }) => {
    try {
      await productApi.deleteCategory(id);
      return id;
    } catch (error) {
      console.warn('删除分类失败，使用模拟数据', error);
      const index = MOCK_CATEGORY_DATA.findIndex(c => c.id === id);
      if (index !== -1) {
        MOCK_CATEGORY_DATA.splice(index, 1);
        return id;
      }
      return rejectWithValue('分类不存在');
    }
  }
);

const productSlice = createSlice({
  name: 'product',
  initialState: {
    productList: [],
    productPagination: {
      current: 1,
      pageSize: 10,
      total: 0
    },
    loading: false,
    error: null
  },
  reducers: {
    setProductPagination: (state, action) => {
      state.productPagination = { ...state.productPagination, ...action.payload };
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取商品列表
      .addCase(fetchProductList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductList.fulfilled, (state, action) => {
        state.loading = false;
        state.productList = action.payload.list;
        state.productPagination = {
          current: action.payload.page,
          pageSize: action.payload.pageSize,
          total: action.payload.total
        };
      })
      .addCase(fetchProductList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.productList = MOCK_PRODUCT_DATA;
        state.productPagination.total = MOCK_PRODUCT_DATA.length;
      })
      // 创建商品
      .addCase(createProduct.fulfilled, (state, action) => {
        state.productList.push(action.payload);
      })
      // 更新商品
      .addCase(updateProduct.fulfilled, (state, action) => {
        const index = state.productList.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.productList[index] = action.payload;
        }
      })
      // 删除商品
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.productList = state.productList.filter(p => p.id !== action.payload);
      })
      // 切换商品状态
      .addCase(toggleProductStatus.fulfilled, (state, action) => {
        const index = state.productList.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.productList[index] = action.payload;
        }
      })
      // 商品审核
      .addCase(auditProduct.fulfilled, (state, action) => {
        const index = state.productList.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.productList[index] = action.payload;
        }
      })
      .addCase(auditProduct.rejected, (state, action) => {
        // Assuming message is available globally or imported elsewhere
        // For now, just log the error
        console.error('商品审核失败：', action.error.message);
      });
  }
});

export const { setProductPagination } = productSlice.actions;

// 分类 Slice
const categorySlice = createSlice({
  name: 'category',
  initialState: {
    categoryList: [],
    categoryPagination: {
      current: 1,
      pageSize: 10,
      total: 0
    },
    loading: false,
    error: null
  },
  reducers: {
    setCategoryPagination: (state, action) => {
      state.categoryPagination = { ...state.categoryPagination, ...action.payload };
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取分类列表
      .addCase(fetchCategoryList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategoryList.fulfilled, (state, action) => {
        state.loading = false;
        state.categoryList = action.payload.list;
        state.categoryPagination = {
          current: action.payload.page,
          pageSize: action.payload.pageSize,
          total: action.payload.total
        };
      })
      .addCase(fetchCategoryList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.categoryList = MOCK_CATEGORY_DATA;
        state.categoryPagination.total = MOCK_CATEGORY_DATA.length;
      })
      // 创建分类
      .addCase(createCategory.fulfilled, (state, action) => {
        state.categoryList.push(action.payload);
      })
      // 更新分类
      .addCase(updateCategory.fulfilled, (state, action) => {
        const index = state.categoryList.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.categoryList[index] = action.payload;
        }
      })
      // 删除分类
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.categoryList = state.categoryList.filter(c => c.id !== action.payload);
      });
  }
});

export const { setCategoryPagination } = categorySlice.actions;

export default productSlice.reducer; 